import type { SignupContext } from '@/utils/analytics'

interface ExposedMixpanel {
  reload: () => void
  trackEvent: (eventName: string, properties?: Record<string, unknown>) => void
  setSignupContext: (context: SignupContext) => void
  resetIdentity: () => void
}

export default defineNuxtPlugin({
  name: 'mixpanel',
  dependsOn: ['cookiebot'],
  async setup() {
    const {
      mode,
      mixpanel: { token, url },
    } = useRuntimeConfig().public

    const mixpanel = reactive<{ client: ExposedMixpanel | null }>({
      client: null,
    })

    if (!token) {
      return {
        provide: {
          mixpanel,
        },
      }
    }

    const mixpanelInstance = shallowRef<typeof import('mixpanel-browser').default | null>(null)

    const datacenter = useSystemStore().platform

    // Cookiebot resolves over the network, so the first pages have usually rendered
    // before there is an instance to track on. Events and the signup context are held
    // here until there is.
    let missedEvents: { name: string; properties?: Record<string, unknown> }[] = []
    let signupContext: SignupContext | null = null

    const initialiseMixpanel = async () => {
      const { default: mpInstance } = await import('mixpanel-browser')

      mixpanelInstance.value = mpInstance

      mixpanelInstance.value.init(token, {
        autocapture: false,
        record_sessions_percent: 0,
        api_host: url,
        debug: mode === 'development',
      })

      mixpanelInstance.value.register({ datacenter, ...signupContext })

      missedEvents.forEach(({ name, properties }) => {
        mixpanelInstance.value?.track(name, properties)
      })
      missedEvents = []
    }

    const resetMixpanel = () => {
      if (!mixpanelInstance.value) return

      mixpanelInstance.value.reset()
      mixpanelInstance.value = null
    }

    const isTrackedEnv = mode === 'production' || mode === 'staging'

    if (isTrackedEnv) {
      const { $cookiebot } = useNuxtApp()

      const hasConsent = computed(() => {
        const cbInstance = $cookiebot.instance
        if (!cbInstance) return false

        return cbInstance.consented && cbInstance.consent.statistics
      })

      watch(
        hasConsent,
        async value => {
          if (value && !mixpanelInstance.value) {
            await initialiseMixpanel()
          } else if (!value) {
            resetMixpanel()
          }
        },
        { immediate: true }
      )
    } else if (mode === 'development' && !isTestRunner()) {
      await initialiseMixpanel()
    }

    const { id: userId } = storeToRefs(useUserStore())
    const { isDirectSign } = storeToRefs(useDirectSignStore())
    const { id: businessId } = storeToRefs(useBusinessStore())

    watchEffect(() => {
      if (userId.value && !isDirectSign.value) {
        mixpanelInstance.value?.identify(userId.value)
      }

      if (businessId.value) {
        // Using snake_case to align with Mixpanel's conventions
        mixpanelInstance.value?.people.set({ business_id: businessId.value })
      }
    })

    const reload = () => {
      mixpanelInstance.value?.track_pageview()
    }

    const trackEvent = (eventName: string, properties?: Record<string, unknown>) => {
      if (!mixpanelInstance.value) {
        missedEvents.push({ name: eventName, properties })
        return
      }

      mixpanelInstance.value.track(eventName, properties)
    }

    const setSignupContext = (context: SignupContext) => {
      signupContext = context
      mixpanelInstance.value?.register(context)
    }

    // Without this the next person on this browser inherits the previous user's distinct id
    const resetIdentity = () => {
      signupContext = null
      missedEvents = []

      if (!mixpanelInstance.value) return

      mixpanelInstance.value.reset()
      // reset() clears the super properties, so the datacenter has to go back on
      mixpanelInstance.value.register({ datacenter })
    }

    mixpanel.client = {
      reload,
      trackEvent,
      setSignupContext,
      resetIdentity,
    }

    return {
      provide: {
        mixpanel,
      },
    }
  },
})
